// src/pages/Reports.js
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "../../components/ui/card";
import API_BASE_URL from "../../utils/config";
import { useAuth } from "../../AuthContext";
import {
  FiAlertCircle,
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
  FiRefreshCw,
  FiTool,
  FiInfo,
} from "react-icons/fi";

/* tiny badge helpers (no extra deps) */
const Badge = ({ tone = "slate", children, className = "" }) => {
  const tones = {
    green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-200",
    slate: "bg-slate-100 text-slate-700 ring-slate-200",
    red: "bg-rose-50 text-rose-700 ring-rose-200",
    blue: "bg-blue-50 text-blue-700 ring-blue-200",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ${tones[tone]} ${className}`}
    >
      {children}
    </span>
  );
};

/** Optional: per-case one-liner notes shown above the table */
const CASE_NOTES = {
  productsWithoutBatches:
    "Products that have no associated batch. Create placeholder batches or link existing ones.",
  productsWithoutCategoryOrGST:
    "Products missing category and/or GST category. Map to defaults or fix master data.",
  invoicesWithoutLinkedOrders:
    "Invoices that are not linked to any order. Link by matching totals or manually map.",
  orderTotalMismatch:
    "Order total differs from the sum of its items. Recalculate totals.",
  orderItemsMiscalculated:
    "Order items total is miscomputed (qty × rate ± GST). Recalculate line totals.",
  invoiceTotalMismatch:
    "Invoice total differs from the sum of its items. Recalculate totals.",
  invoiceItemsMiscalculated:
    "Invoice items total is miscomputed (qty × rate ± GST). Recalculate line totals.",
  invoiceVsOrderTotalMismatch:
    "Invoice total and linked order total differ. Normalize both after item sync.",
  doctorMultiCustomers:
    "Doctors linked to multiple customers. Keep the primary or split correctly.",
  batchQuantityMismatch:
    "Stored batch stock != (purchases + credits − invoices). Fix brings stored to computed.",
};

/** Nicely title-case keys */
const formatHeader = (key) =>
  key
    .replace(/^case\d+_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

/** Lightweight numeric detection/format */
const isNumeric = (v) =>
  (typeof v === "number" && Number.isFinite(v)) ||
  (typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v)));

const fmtNum = (v) => {
  if (!isNumeric(v)) return String(v);
  const n = Number(v);
  // If integer, show no decimals; otherwise 2 decimals
  return Number.isInteger(n) ? n.toString() : n.toFixed(2);
};

export default function Reports() {
  const { user } = useAuth();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [fixing, setFixing] = useState({});
  const [confirmModal, setConfirmModal] = useState({ open: false, caseName: "" });

  const isAdmin = (user?.role || "").toLowerCase() === "admin";

  /** Which validation cases can be auto-repaired */
  const canFix = (caseName) => {
    const fixableCases = [
      "productsWithoutBatches",
      "productsWithoutCategoryOrGST",
      "invoicesWithoutLinkedOrders",
      "orderTotalMismatch",
      "orderItemsMiscalculated",
      "invoiceTotalMismatch",
      "invoiceItemsMiscalculated",
      "invoiceVsOrderTotalMismatch",
      "doctorMultiCustomers",
      "batchQuantityMismatch", // ⬅️ New case repair enabled
    ];
    return fixableCases.includes(caseName);
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const tokenStored = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE_URL}/api/reports/validations`, {
        headers: { Authorization: `Bearer ${tokenStored}` },
      });
      setData(res.data || {});
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const allKeys = useMemo(() => Object.keys(data || {}), [data]);
  const allExpanded = useMemo(
    () => allKeys.length > 0 && allKeys.every((k) => expanded[k]),
    [allKeys, expanded]
  );

  if (!isAdmin) return <div className="p-6">🚫 Access denied</div>;

  /** Cell renderer with small case-aware styling */
  const renderCell = (caseName, col, val, row) => {
    // For batchQuantityMismatch, color the diff cell: positive (need to increase), negative (need to decrease)
    if (caseName === "batchQuantityMismatch" && String(col).toLowerCase() === "diff") {
      const num = Number(val);
      const tone = num > 0 ? "blue" : "red"; // positive: computed > stored (increase); negative: computed < stored (decrease)
      const sign = num > 0 ? "+" : "";
      return <Badge tone={tone}>{sign + fmtNum(num)}</Badge>;
    }

    // Generic numeric monospace
    if (isNumeric(val)) {
      return <span className="font-[tabular-nums]">{fmtNum(val)}</span>;
    }

    return String(val);
  };

  return (
    <div className="p-4 md:p-6 space-y-5">
      {/* Header + actions */}
      <div className="mx-auto max-w-6xl">
        <div className="mb-3 flex items-center justify-between">
          <h1 className="text-2xl font-bold tracking-tight">Reports &amp; Data Validation</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchReports}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm hover:bg-slate-50"
            >
              <FiRefreshCw className="animate-[spin_1.2s_linear_infinite] motion-reduce:animate-none" />
              Refresh
            </button>
            <button
              onClick={() => {
                const next = {};
                allKeys.forEach((k) => (next[k] = !allExpanded));
                setExpanded(next);
              }}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
            >
              {allExpanded ? <FiChevronUp /> : <FiChevronDown />}
              {allExpanded ? "Collapse All" : "Expand All"}
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="h-4 w-48 animate-pulse rounded bg-slate-200" />
            <div className="mt-4 space-y-2">
              <div className="h-10 w-full animate-pulse rounded bg-slate-100" />
              <div className="h-10 w-full animate-pulse rounded bg-slate-100" />
              <div className="h-10 w-full animate-pulse rounded bg-slate-100" />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {allKeys.map((caseName) => {
              const issues = data[caseName] || [];
              const isOpen = expanded[caseName] ?? false;
              const fixable = canFix(caseName);
              const hasIssues = Array.isArray(issues) && issues.length > 0;

              return (
                <Card key={caseName} className="border-slate-200 shadow-sm">
                  <CardContent className="p-0">
                    {/* Header row */}
                    <button
                      onClick={() =>
                        setExpanded((prev) => ({ ...prev, [caseName]: !isOpen }))
                      }
                      className="flex w-full items-center justify-between gap-3 rounded-t-2xl px-4 py-4 text-left md:px-6"
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className={`grid h-9 w-9 place-items-center rounded-lg ${
                            hasIssues
                              ? "bg-amber-100 text-amber-700"
                              : "bg-emerald-100 text-emerald-700"
                          }`}
                        >
                          {hasIssues ? <FiAlertCircle /> : <FiCheckCircle />}
                        </span>
                        <div>
                          <div className="font-semibold text-slate-900">
                            {formatHeader(caseName)}
                          </div>
                          <div className="mt-0.5 text-xs text-slate-500 flex items-center gap-1">
                            <FiInfo className="shrink-0" />
                            <span>
                              {CASE_NOTES[caseName] ||
                                (hasIssues
                                  ? "Review and repair the listed records."
                                  : "No problems detected.")}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {hasIssues ? (
                          <Badge tone="amber">
                            {issues.length} issue{issues.length > 1 ? "s" : ""}
                          </Badge>
                        ) : (
                          <Badge tone="green">No issues found</Badge>
                        )}
                        <span className="text-slate-400">
                          {isOpen ? <FiChevronUp /> : <FiChevronDown />}
                        </span>
                      </div>
                    </button>

                    {/* Body */}
                    {isOpen && (
                      <div className="px-4 pb-5 pt-2 md:px-6">
                        {/* Actions */}
                        <div className="mb-3 flex items-center justify-between">
                          <div className="text-sm text-slate-500">
                            {hasIssues ? "Details" : "Everything is healthy here."}
                          </div>
                          {fixable && hasIssues && (
                            <button
                              onClick={() => setConfirmModal({ open: true, caseName })}
                              disabled={fixing[caseName]}
                              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <FiTool />
                              {fixing[caseName] ? "Repairing…" : "Repair"}
                            </button>
                          )}
                        </div>

                        {/* Table / success message */}
                        {hasIssues ? (
                          <div className="overflow-auto rounded-lg border border-slate-200">
                            <table className="min-w-full text-sm">
                              <thead className="sticky top-0 z-10 bg-slate-50 text-slate-700">
                                <tr>
                                  {Object.keys(issues[0]).map((col) => (
                                    <th
                                      key={col}
                                      className="whitespace-nowrap border-b border-slate-200 px-3 py-2 text-left font-medium"
                                    >
                                      {formatHeader(col)}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {issues.map((row, i) => (
                                  <tr
                                    key={i}
                                    className="odd:bg-white even:bg-slate-50 hover:bg-blue-50/50"
                                  >
                                    {Object.entries(row).map(([col, val], j) => (
                                      <td
                                        key={j}
                                        className="whitespace-nowrap border-t border-slate-100 px-3 py-2 text-slate-800"
                                      >
                                        {renderCell(caseName, col, val, row)}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-800">
                            <FiCheckCircle />
                            <span className="text-sm">No issues found</span>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}

            {allKeys.length === 0 && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-600">
                No report data returned.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {confirmModal.open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-slate-900">Confirm Repair</h2>
            <p className="mt-2 text-sm text-slate-600">
              Are you sure you want to repair{" "}
              <span className="font-medium text-slate-900">
                “{formatHeader(confirmModal.caseName)}”
              </span>
              ?
            </p>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirmModal({ open: false, caseName: "" })}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const caseName = confirmModal.caseName;
                  setFixing((s) => ({ ...s, [caseName]: true }));
                  try {
                    const tokenStored = localStorage.getItem("token");
                    const res = await axios.post(
                      `${API_BASE_URL}/api/reports/correction/${caseName}`,
                      {},
                      { headers: { Authorization: `Bearer ${tokenStored}` } }
                    );
                    alert(res.data?.message || "Case repaired successfully!");
                    setConfirmModal({ open: false, caseName: "" });
                    fetchReports();
                  } catch (err) {
                    console.error(err);
                    alert("Error repairing case: " + (err?.message || "Unknown error"));
                    setConfirmModal({ open: false, caseName: "" });
                  } finally {
                    setFixing((s) => ({ ...s, [caseName]: false }));
                  }
                }}
                className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
